import { RiCloseLine } from '@remixicon/react';
import { LayerId, LayerType, SerializedLayers } from '@lidojs/design-core';
import { useEditor } from '@lidojs/design-editor';
import React, { FC } from 'react';
import { isMobile } from 'react-device-detect';
import { v4 } from 'uuid';
import { Line, lines } from './data/line';
import { Shape, shapes } from './data/shape';
import IconButton from '../../IconButton';
import { useTranslation } from 'react-i18next';

const ShapeContent: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  const { actions, query } = useEditor();
  const addLine = (props: Line['props']) => {
    actions.addLineLayer({ props });

    if (isMobile) {
      onClose();
    }
  };
  const addShape = (shape: Shape) => {
    actions.addShapeLayer({
      type: {
        resolvedName: 'ShapeLayer',
      },
      props: {
        shape: shape.type,
        position: {
          x: 0,
          y: 400,
        },
        boxSize: {
          width: shape.width,
          height: shape.height,
        },
        rotate: 0,
        color: '#171717',
      },
    });
    if (isMobile) {
      onClose();
    }
  };

  const handleDrag = (event: React.DragEvent, shape: Shape) => {
    const { clientX, clientY } = event;
    const id = v4();
    const data: {
      layer: LayerType;
      data: { rootId: LayerId; layers: SerializedLayers };
    } = {
      layer: 'Shape',
      data: {
        rootId: id,
        layers: {
          [id]: {
            type: {
              resolvedName: 'ShapeLayer',
            },
            locked: false,
            parent: 'ROOT',
            child: [],
            props: {
              shape: shape.type,
              position: {
                x: 0,
                y: 400,
              },
              boxSize: {
                width: shape.width,
                height: shape.height,
              },
              rotate: 0,
              color: '#171717',
            },
          },
        },
      },
    };
    actions.startDragNDrop(data, { x: clientX, y: clientY });
    event.dataTransfer.clearData('text/plain');
    event.dataTransfer.setData('text/plain', JSON.stringify(data));
    event.dataTransfer.setDragImage(new Image(), 0, 0);
  };

  const handleDragLine = (event: React.DragEvent, line: Line) => {
    const { clientX, clientY } = event;
    const width = query.getPageSize().width / 2;
    const data: {
      layer: LayerType;
      data: { rootId: LayerId; layers: SerializedLayers };
    } = {
      layer: 'Line',
      data: {
        rootId: line.id,
        layers: {
          [line.id]: {
            props: {
              ...line.props,
              boxSize: {
                width,
                height: 4,
              },
              position: {
                x: 0,
                y: 0,
              },
              style: 'solid',
              color: 'rgb(0, 0, 0)',
              scale: 1,
              rotate: 0,
            },
            type: {
              resolvedName: 'LineLayer',
            },
            locked: false,
            parent: 'ROOT',
            child: [],
          },
        },
      },
    };
    actions.startDragNDrop(data, { x: clientX, y: clientY });
    event.dataTransfer.clearData('text/plain');
    event.dataTransfer.setData('text/plain', JSON.stringify(data));
    event.dataTransfer.setDragImage(new Image(), 0, 0);
  };

  return (
    <div
      css={(theme) => ({
        width: '100%',
        height: '100%',
        flexDirection: 'column',
        overflowY: 'auto',
        display: 'flex',
        color: theme.colors.black,
      })}
    >
      <div
        css={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          height: 50,
          borderBottom: `1px solid ${theme.colors.stroke}`,
          padding: '0 20px',
        })}
      >
        <p
          css={(theme) => ({
            lineHeight: '48px',
            fontWeight: 600,
            color: theme.colors.content,
            flexGrow: 1,
          })}
        >
          {t('page_editor.general.shape')}
        </p>
        <IconButton onClick={onClose}>
          <RiCloseLine size={20} />
        </IconButton>
      </div>
      <div
        css={(theme) => ({
          padding: '16px',
        })}
      >
        <div
          css={(theme) => ({
            padding: '8px 0',
            fontWeight: 700,
            color: theme.colors.content,
          })}
        >
          {t('page_editor.shape.arrow')}
        </div>
        <div
          css={(theme) => ({
            flexGrow: 1,
            overflowY: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(4,minmax(0,1fr))',
            gridGap: 8,
            paddingBottom: '10px',
          })}
        >
          {lines.map((l, idx) => (
            <div
              key={idx}
              draggable
              css={(theme) => ({
                width: '100%',
                paddingBottom: '100%',
                position: 'relative',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '16px',
                aspectRatio: '1/1',
                borderRadius: 5,
                transition: 'box-shadow 0.3s',
                ':hover': {
                  boxShadow: theme.shadow.hoverItem,
                },
                'html.dark &': {
                  ':hover': {
                    boxShadow: 'none',
                    filter: 'brightness(100%)',
                  },
                  backgroundColor: theme.colors.backgroundItem,
                  filter: 'brightness(90%)',
                },
                '-webkit-user-drag': 'element',
              })}
              onClick={() => addLine(l.props)}
              onDragStart={(e) => handleDragLine(e, l)}
            >
              <div
                css={(theme) => ({
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  width: '100%',
                  padding: '8px',
                  '& svg': {
                    width: '100%',
                    height: '100%',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  },
                })}
              >
                  {l.icon}
              </div>
            </div>
          ))}
        </div>
        <div
          css={(theme) => ({
            padding: '8px 0',
            fontWeight: 700,
            color: theme.colors.content,
          })}
        >
          {t('page_editor.shape.shape')}
        </div>
        <div
          css={(theme) => ({
            flexGrow: 1,
            overflowY: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(4,minmax(0,1fr))',
            gridGap: 8,
          })}
        >
          {shapes.map((shape) => (
            <div
              key={shape.type}
              css={(theme) => ({
                width: '100%',
                paddingBottom: '100%',
                position: 'relative',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '16px',
                aspectRatio: '1/1',
                borderRadius: 5,
                transition: 'box-shadow 0.3s',
                ':hover': {
                  boxShadow: theme.shadow.hoverItem,
                },
                'html.dark &': {
                  ':hover': {
                    boxShadow: 'none',
                    filter: 'brightness(100%)',
                  },
                  backgroundColor: theme.colors.backgroundItem,
                  filter: 'brightness(90%)',
                },
                '-webkit-user-drag': 'element',
              })}
              onClick={() => addShape(shape)}
              onDragStart={(e) => handleDrag(e, shape)}
            >
              {shape.icon}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ShapeContent;
